import { useCallback } from 'react';

import { fetchEndpointData } from 'api';
import { useToastContext } from 'contexts';
import type { CartId } from 'types';
import { is, sleep } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { joinPath } from 'utils/url';

import { useGlobalLinks } from './useSitecoreContext';

type ListType =
	| { cartId: CartId | undefined; list: 'cart' }
	| { list: 'wishlist' };
export function useShareList(type: ListType) {
	const { sendToast } = useToastContext();
	const { t } = useI18n();
	const { wishlist } = useGlobalLinks();

	const shareList = useCallback(
		async (callback: (url: string) => Promise<unknown>) => {
			let listShareId: string | undefined;
			try {
				if (!wishlist) {
					throw new Error('Wishlist global link missing');
				}

				if (type.list === 'wishlist') {
					listShareId = await fetchEndpointData('PostShareWishlist');
				} else if (type.list === 'cart' && type.cartId) {
					listShareId = await fetchEndpointData({
						name: 'PostShareCart',
						pathParams: { cartId: type.cartId },
					});
				} else {
					throw new Error('List type error or missing cart id');
				}

				if (!listShareId) {
					// TODO: Remove in some future where fetchEndpointData throws on empty.
					throw new Error('No list share id');
				}
				// Safari being insane - a setTimeout hack works around permission error.
				// https://stackoverflow.com/q/62327358
				await sleep(0);

				const url = joinPath(
					globalThis.location.origin,
					wishlist,
					listShareId,
					'/',
				);

				await callback(url);
			} catch {
				sendToast({ message: t('list_share_error'), variant: 'error' });
				throw new Error('List share error');
			}

			sendToast({ message: t('list_share_success'), variant: 'success' });
		},
		[sendToast, t, type, wishlist],
	);

	const shareListUrl = useCallback(
		() =>
			shareList((url: string) => {
				// oxlint-disable-next-line typescript/unbound-method
				if (is.func(globalThis.navigator.share)) {
					return globalThis.navigator.share({
						url,
					});
				}
				return globalThis.navigator.clipboard.writeText(url);
			}),
		[shareList],
	);
	const copyListUrl = useCallback(async () => {
		await shareList((url: string) =>
			globalThis.navigator.clipboard.writeText(url),
		);
	}, [shareList]);

	return { shareListUrl, copyListUrl } as const;
}
